import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

const normalizeItems = (items) => {
  const normalized = (items || [])
    .map((item) => {
      if (typeof item === "string") return { name: item, value: null };
      if (item && typeof item === "object" && "name" in item) {
        return { name: item.name, value: item.value ?? null };
      }
      return null;
    })
    .filter((item) => item && typeof item.name === "string" && item.name.trim() !== "");

  const merged = normalized.reduce((acc, item) => {
    const key = item.name.trim().toLowerCase();
    const existing = acc.get(key);
    if (!existing) {
      acc.set(key, { ...item, name: item.name.trim() });
      return acc;
    }
    const nextValue =
      typeof existing.value === "number" || typeof item.value === "number"
        ? (Number(existing.value) || 0) + (Number(item.value) || 0)
        : existing.value ?? item.value ?? null;
    acc.set(key, { ...existing, value: nextValue });
    return acc;
  }, new Map());

  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const formatDateValue = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const normalizeDoctors = (doctors) =>
  (doctors || [])
    .map((doctor) => {
      const monthlyNotes = typeof doctor?.monthly_notes === "number"
        ? doctor.monthly_notes
        : Number(doctor?.monthly_notes || doctor?.monthlyNotes || 0);

      return {
        name: doctor?.user_name || doctor?.userName || doctor?.name || "—",
        email: doctor?.user_email || doctor?.userEmail || doctor?.email || "—",
        organization: doctor?.organization || "—",
        specialty: doctor?.specialty || "—",
        superOrg: doctor?.super_org || doctor?.superOrg || "—",
        createdAt: formatDateValue(doctor?.created_at || doctor?.createdAt),
        recentNote: formatDateValue(doctor?.recent_note || doctor?.recentNote),
        monthlyNotes,
      };
    })
    .sort((a, b) => b.monthlyNotes - a.monthlyNotes);

const customerPricing = {
  // Ironwood: { costPerUser: 200 },
  // TenOnc: { costPerUser: 250 },
  // Astera: { costPerUser: 300, aiBillingPrice: 100, aiBillingCount: 3 },
  // PCSRI: { costPerUser: 300 },
  // LACN: { costPerUser: 300 },
  // MOASD: { costPerUser: 300 },
  // cCARE: { costPerUser: 300, integrationMaintenance: 1250, aiBillingPrice: 100, aiBillingCount: 5 },
  // "Stripe 4D EMR": { costPerUser: 200 },
  // "Stripe standalone": { costPerUser: 150 },
};

const formatCurrency = (value) =>
  typeof value === "number"
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : "—";

const buildCustomerPricingSummary = (customerName, doctorsCount) => {
  const pricing = customerPricing[customerName];
  if (!pricing) {
    return { total: null, breakdown: "Custom pricing" };
  }

  const perUser = pricing.costPerUser ?? 0;
  const maintenance = pricing.integrationMaintenance ?? 0;
  const aiBillingPrice = pricing.aiBillingPrice ?? 0;
  const aiBillingCount = pricing.aiBillingCount ?? 0;
  const aiBillingTotal = aiBillingPrice * aiBillingCount;
  const total = doctorsCount * perUser + maintenance + aiBillingTotal;

  const breakdownParts = [];
  breakdownParts.push(`${formatCurrency(perUser)} x ${doctorsCount}`);
  if (maintenance) breakdownParts.push(`${formatCurrency(maintenance)}`);
  if (aiBillingPrice && aiBillingCount) {
    breakdownParts.push(`${formatCurrency(aiBillingPrice)} x ${aiBillingCount}`);
  }

  return { total, breakdown: breakdownParts.join(" + ") };
};

const SpecialtyResultsTable = ({ title, subtitle, doctors, loading, onBack }) => (
  <div className="fixed inset-0 bg-white z-[30] flex flex-col">
    <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to search"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-ktx-purple">{title}</h1>
            <p className="text-sm text-gray-600">{subtitle}</p>
          </div>
        </div>
      </div>
    </div>

    <div className="flex-1 min-h-0 p-4">
      <div className="h-full border border-gray-200 rounded-lg overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-[35]">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Email</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Created At</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Recent Note</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Monthly Notes</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Billable Organization</th>
              {/* <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Organization</th> */}
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Specialty</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <span className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-ktx-purple inline-block"></span>
                    <span className="text-gray-500">Loading doctors...</span>
                  </div>
                </td>
              </tr>
            ) : doctors.length ? (
              doctors.map((doctor, index) => (
                <tr
                  className={`hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-25"}`}
                  key={`${doctor.email}-${index}`}
                >
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.name}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.email}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.createdAt}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.recentNote}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.monthlyNotes}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.superOrg}</td>
                  {/* <td className="py-3 px-4 border-b border-gray-100">{doctor.organization}</td> */}
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.specialty}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center py-12 text-gray-500">
                  No doctors found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const CustomerResultsTable = ({ title, subtitle, doctors, loading, onBack }) => (
  <div className="fixed inset-0 bg-white z-[30] flex flex-col">
    <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back to search"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-ktx-purple">{title}</h1>
            <p className="text-sm text-gray-600">{subtitle}</p>
          </div>
        </div>
      </div>
    </div>

    <div className="flex-1 min-h-0 p-4">
      <div className="h-full border border-gray-200 rounded-lg overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-[35]">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Email</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Created At</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Recent Note</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Monthly Notes</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Specialty</th>
              {/* <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Organization</th> */}
              <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Billable Organization</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td colSpan={2} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <span className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-ktx-purple inline-block"></span>
                    <span className="text-gray-500">Loading doctors...</span>
                  </div>
                </td>
              </tr>
            ) : doctors.length ? (
              doctors.map((doctor, index) => (
                <tr
                  className={`hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-25"}`}
                  key={`${doctor.email}-${index}`}
                >
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.name}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.email}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.createdAt}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.recentNote}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.monthlyNotes}</td>
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.specialty}</td>
                  {/* <td className="py-3 px-4 border-b border-gray-100">{doctor.organization}</td> */}
                  <td className="py-3 px-4 border-b border-gray-100">{doctor.superOrg}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="text-center py-12 text-gray-500">
                  No doctors found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const RevOpsDoctorSearchbarBase = forwardRef(({
  asyncFunction,
  fetchDoctors,
  formLabel,
  defaultOptionLabel = "Show all",
  selectClassName,
  inputClassName,
  submitClassName,
  tableTitle,
  subtitleFormatter,
  ResultsTable,
  itemFilter,
}, ref) => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [selectionLabel, setSelectionLabel] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (asyncFunction) {
      (async () => {
        const data = await asyncFunction();
        const normalized = normalizeItems(data);
        setItems(itemFilter ? normalized.filter(itemFilter) : normalized);
      })();
    }
  }, [asyncFunction, itemFilter]);

  const refinedItems = useMemo(() => {
    if (!query) return items;
    return items.filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [items, query]);

  const performSubmit = async (selectionOverride) => {
    setSubmitted(true);
    setLoadingDoctors(true);
    try {
      const selection = selectionOverride ?? (selectedItem || query);
      const meta = items.find((item) => item.name === selection) || null;
      setSelectedMeta(meta);
      setSelectionLabel(selection);
      const data = selection ? await fetchDoctors(selection) : [];
      setDoctors(normalizeDoctors(data));
    } finally {
      setLoadingDoctors(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await performSubmit();
  };

  const submitSelection = async (selection) => {
    if (!selection) return;
    setQuery(selection);
    setSelectedItem(selection);
    setShowSuggestions(false);
    await performSubmit(selection);
  };

  useImperativeHandle(ref, () => ({
    submitSelection,
  }));

  const resetForm = () => {
    setSubmitted(false);
    setQuery("");
    setSelectedItem("");
    setDoctors([]);
    setSelectedMeta(null);
    setSelectionLabel("");
  };

  return (
    <>
      {!submitted && (
        <form className="w-full max-w-[320px] mr-auto space-y-3" onSubmit={handleSubmit}>
          <div className="relative">
            <input
              className={`text-[13px] w-full rounded-md border ${inputClassName || "border-slate-200 focus:border-slate-400 focus:ring-1 focus:ring-slate-200"} bg-white text-slate-900 placeholder:text-slate-400`}
              type="text"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setShowSuggestions(true);
              }}
              placeholder="Search"
            />
            {showSuggestions && query && refinedItems.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg z-10">
                {refinedItems.slice(0, 8).map((item) => (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => {
                      setSelectedItem(item.name);
                      setQuery(item.name);
                      setShowSuggestions(false);
                    }}
                    className="text-[13px] w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            disabled={selectedItem === "" && query === ""}
            className={`w-full rounded-md border border-[1px] font-semibold py-2 transition-colors ${submitClassName || "border-sage-green text-sage-green hover:bg-sage-green hover:text-white"}`}
            type="submit"
          >
            Submit
          </button>
        </form>
      )}

      {submitted && (
        <ResultsTable
          title={tableTitle(selectionLabel || selectedItem || query)}
          subtitle={subtitleFormatter
            ? subtitleFormatter({ doctorsCount: doctors.length, selectedMeta, selectionLabel })
            : `${doctors.length} doctors`
          }
          doctors={doctors}
          loading={loadingDoctors}
          onBack={resetForm}
        />
      )}
    </>
  );
});

export const RevOpsSpecialtyDoctorSearchbar = forwardRef(({ asyncFunction, fetchDoctors }, ref) => {
  const stableItemFilter = useCallback(
    (item) => item.name !== "N/A" && item.name !== "Unknown",
    []
  );

  return (
    <RevOpsDoctorSearchbarBase
      ref={ref}
      asyncFunction={asyncFunction}
      fetchDoctors={fetchDoctors}
      formLabel="Specialty Navigator"
      defaultOptionLabel="All specialties"
      inputClassName="border-emerald-200 focus:border-emerald-400 focus:ring-emerald-200 bg-emerald-50/40"
      selectClassName="border-emerald-200 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200"
      submitClassName="border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white"
      tableTitle={(selection) => selection || "Specialty Doctors"}
      subtitleFormatter={({ doctorsCount }) => `${doctorsCount} doctors`}
      ResultsTable={SpecialtyResultsTable}
      itemFilter={stableItemFilter}
    />
  );
});
RevOpsSpecialtyDoctorSearchbar.displayName = "RevOpsSpecialtyDoctorSearchbar";

export const RevOpsCustomerDoctorSearchbar = forwardRef(({ asyncFunction, fetchDoctors }, ref) => (
  <RevOpsDoctorSearchbarBase
    ref={ref}
    asyncFunction={asyncFunction}
    fetchDoctors={fetchDoctors}
    formLabel="Customer Navigator"
    defaultOptionLabel="All customers"
    inputClassName="border-sky-200 focus:border-sky-400 focus:ring-sky-200 bg-sky-50/40"
    selectClassName="border-sky-200 focus:border-sky-400 focus:ring-1 focus:ring-sky-200"
    submitClassName="border-sky-500 text-sky-600 hover:bg-sky-500 hover:text-white"
    tableTitle={(selection) => selection || "Customer Doctors"}
    subtitleFormatter={({ doctorsCount, selectionLabel }) => {
      const summary = buildCustomerPricingSummary(selectionLabel, doctorsCount);
      if (!summary.total) {
        return `${doctorsCount} doctors • ${summary.breakdown}`;
      }
      return `${doctorsCount} doctors • ${formatCurrency(summary.total)} (${summary.breakdown})`;
    }}
    ResultsTable={CustomerResultsTable}
  />
));
RevOpsCustomerDoctorSearchbar.displayName = "RevOpsCustomerDoctorSearchbar";
